import { useState } from "react";
import { CodeEditor } from "../CodeEditor";
import { StatusDot } from "../StatusDot";
import { Text } from "../Text";
import { buildDiffHunks, type MergedFile } from "../stepFilesDiff";
import { ChatDisclosure } from "./ChatDisclosure";
import { ToolRow } from "./ToolRow";
import { ToolStatus } from "./ToolStatus";

export function PublishedReviewFileList({
  files,
}: {
  files: readonly MergedFile[];
}) {
  const [expandedPath, setExpandedPath] = useState<string | null>(null);
  const sorted = files.toSorted((a, b) => a.path.localeCompare(b.path));
  if (files.length === 0) {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        No file changes in this version.
      </Text>
    );
  }
  return sorted.map((file, index) => {
    const expanded = expandedPath === file.path;
    const hunks = expanded
      ? buildDiffHunks(file.previousContent ?? "", file.currentContent ?? "")
      : [];
    const verb =
      file.kind === "added"
        ? "Added"
        : file.kind === "removed"
          ? "Removed"
          : "Modified";
    return (
      <ToolRow
        key={file.path}
        variant={index < sorted.length - 1 ? "branch" : "terminate"}
      >
        <ChatDisclosure
          header={
            <ToolStatus
              icon={
                <StatusDot
                  color={
                    file.kind === "added"
                      ? "green"
                      : file.kind === "removed"
                        ? "red"
                        : "orange"
                  }
                  size={12}
                  halo={false}
                />
              }
              text={`${verb} ${file.path}`}
            />
          }
          expanded={expanded}
          onToggle={() => setExpandedPath(expanded ? null : file.path)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {hunks.map((hunk) => (
              <CodeEditor
                key={hunk.fromDocLine}
                value={hunk.doc}
                filePath={file.path}
                diffRanges={hunk.diffRanges}
                lineNumberMap={[
                  ...hunk.lineOrigins.map((line) => line.sourceLine),
                  hunk.lineOrigins.at(-1)!.sourceLine + 1,
                ]}
                readOnly
              />
            ))}
          </div>
        </ChatDisclosure>
      </ToolRow>
    );
  });
}
